import { useConfirmAndLogEntryDelete } from '@monorepo/logs/src/hooks/useConfirmAndLogEntryDelete';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { ActionMenu } from '@monorepo/shared/componentsV2/menus/ActionMenu';
import { useToast } from '@monorepo/shared/contexts';
import { useLinkOnSameProject } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameProject';
import { copyToClipboard } from '@monorepo/shared/utils/copyToClipboard';
import { format } from 'date-fns';
import { localEquivalentOfUTC } from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const ActionsCell = styled.div<{ $alignMenusToEnd: boolean }>`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: ${({ $alignMenusToEnd }) =>
    $alignMenusToEnd ? 'flex-end' : 'center'};
  width: ${({ $alignMenusToEnd }) => ($alignMenusToEnd ? '100%' : 'auto')};
`;

const ReviewAiEntryButton = styled(Button)`
  white-space: nowrap;
`;

interface LogEntryTableActionsProps {
  alignMenusToEnd: boolean;
  logEntry: LogEntry;
  onEditEntry: (logEntryId: string) => void;
  onViewEntrySource: (edpAiSubmissionId: string, logEntryId: string) => void;
}

export function LogEntryTableActions({
  alignMenusToEnd,
  logEntry,
  onEditEntry,
  onViewEntrySource,
}: LogEntryTableActionsProps) {
  const { logId, organizationId, projectId } = useParams();
  if (!logId || !organizationId || !projectId) {
    throw new Error(
      "LogEntryTableActions was rendered with a url that doesn't have logId, organizationId or projectId",
    );
  }

  const formattedLogDate = useMemo(
    () => format(localEquivalentOfUTC(new Date(logEntry.logDate)), 'Pp'),
    [logEntry.logDate],
  );

  const { handleDelete } = useConfirmAndLogEntryDelete({
    logDate: logEntry.logDate,
    logEntryId: logEntry.id,
    logId,
    organizationId,
    projectId,
  });

  const { edpAiSubmissionId, reviewedBy } = logEntry;
  const needsAiReview = !!edpAiSubmissionId && !reviewedBy;

  const editOption = useMemo(
    () => ({
      displayName: 'Edit',
      onClick: () =>
        edpAiSubmissionId
          ? onViewEntrySource(edpAiSubmissionId, logEntry.id)
          : onEditEntry(logEntry.id),
    }),
    [edpAiSubmissionId, logEntry.id, onEditEntry, onViewEntrySource],
  );

  const singleEntryLink = useLinkOnSameProject(
    `/logs/${logId}/records/${logEntry.id}`,
  );
  const { success } = useToast();
  const copyLinkOption = useMemo(
    () => ({
      displayName: 'Copy link',
      onClick: async () => {
        await copyToClipboard(`${process.env.APP_URL}${singleEntryLink}`);
        success('Link to this Log entry has been copied to clipboard');
      },
    }),
    [singleEntryLink, success],
  );

  const deleteOption = useMemo(
    () => ({
      displayName: 'Delete',
      onClick: handleDelete,
    }),
    [handleDelete],
  );

  const options = useMemo(
    () => [editOption, copyLinkOption, deleteOption],
    [copyLinkOption, deleteOption, editOption],
  );

  return (
    <ActionsCell $alignMenusToEnd={alignMenusToEnd}>
      {needsAiReview && edpAiSubmissionId && (
        <ReviewAiEntryButton
          color="primary"
          onClick={() => onViewEntrySource(edpAiSubmissionId, logEntry.id)}
          variant="text"
        >
          Review AI entry
        </ReviewAiEntryButton>
      )}
      <ActionMenu
        altText={`Action menu for Log entry on ${formattedLogDate}`}
        options={options}
      />
    </ActionsCell>
  );
}
